import { store } from '../store.js';

export const SCHEMA_VERSION = 10;

export const migrateCurrentLineup = (players = []) => (
  players.map((slot) => {
    if (!slot || typeof slot !== 'object') {
      return slot;
    }
    if (slot.rotationPosition !== undefined) {
      return slot;
    }
    if (slot.onCourtPosition !== undefined) {
      const nextSlot = { ...slot, rotationPosition: slot.onCourtPosition };
      delete nextSlot.onCourtPosition;
      return nextSlot;
    }
    if (slot.position === undefined) {
      return slot;
    }
    const nextSlot = { ...slot, rotationPosition: slot.position };
    delete nextSlot.position;
    return nextSlot;
  })
);

export const normalizeRoster = (match) => {
  if (!match || typeof match !== 'object') {
    return match;
  }
  const roster = Array.isArray(match.roster) ? match.roster : [];
  const liberos = Array.isArray(match.liberos) ? match.liberos : [];
  const rosterById = new Map();
  roster.forEach(player => {
    if (!player || player.id === undefined) {
      return;
    }
    rosterById.set(player.id, { ...player, playerType: player.playerType || 'regular' });
  });
  liberos.forEach(player => {
    if (!player || player.id === undefined) {
      return;
    }
    rosterById.set(player.id, { ...player, playerType: 'libero' });
  });
  const nextMatch = { ...match, roster: Array.from(rosterById.values()) };
  if ('liberos' in nextMatch) {
    delete nextMatch.liberos;
  }
  return nextMatch;
};

export const normalizePointLiberoNumber = (point) => {
  if (!point || typeof point !== 'object') {
    return point;
  }
  let nextPoint = point;
  if (point.libero && typeof point.libero === 'object' && point.libero.number !== undefined) {
    nextPoint = { ...point, libero: point.libero.number };
  }
  if ('currentRotation' in nextPoint || 'basePositions' in nextPoint || 'currentPositions' in nextPoint || 'libero' in nextPoint) {
    const renamed = { ...nextPoint };
    if ('currentRotation' in renamed) {
      renamed.currentRotationCode = renamed.currentRotation;
      delete renamed.currentRotation;
    }
    if ('basePositions' in renamed) {
      renamed.currentLineupNumbers = renamed.basePositions;
      delete renamed.basePositions;
    }
    if ('currentPositions' in renamed) {
      renamed.currentOnCourtNumbers = renamed.currentPositions;
      delete renamed.currentPositions;
    }
    if ('libero' in renamed) {
      renamed.currentLiberoNumber = renamed.libero;
      delete renamed.libero;
    }
    return renamed;
  }
  return nextPoint;
};

export const sanitizePlayerObject = (player) => {
  if (!player) {
    return {};
  }
  if (typeof player === 'string') {
    return player;
  }
  if (player.name === null || player.name === undefined) {
    return { ...player, name: '' };
  }
  return player;
};

export const sanitizePoint = (point) => {
  if (!point || typeof point !== 'object') {
    return point;
  }
  const sanitized = { ...point };

  if (sanitized.serveAction) {
    sanitized.serveAction = {
      ...sanitized.serveAction,
      player: sanitizePlayerObject(sanitized.serveAction.player)
    };
  }

  if (sanitized.receiveAction) {
    sanitized.receiveAction = {
      ...sanitized.receiveAction,
      player: sanitizePlayerObject(sanitized.receiveAction.player)
    };
  }

  if (sanitized.scoreAction) {
    sanitized.scoreAction = {
      ...sanitized.scoreAction,
      player: sanitizePlayerObject(sanitized.scoreAction.player)
    };
    if (sanitized.scoreAction.secondPlayer) {
      sanitized.scoreAction.secondPlayer = sanitizePlayerObject(sanitized.scoreAction.secondPlayer);
    }
  }

  if (sanitized.duringRallyRoster && typeof sanitized.duringRallyRoster === 'object') {
    const roster = { ...sanitized.duringRallyRoster };
    Object.keys(roster).forEach(key => {
      if (roster[key] === null || roster[key] === undefined) {
        roster[key] = '';
      }
    });
    sanitized.duringRallyRoster = roster;
  }

  return sanitized;
};

export const migrateState = (saved) => {
  if (!saved || typeof saved !== 'object') {
    return null;
  }
  const version = saved.schemaVersion ?? 1;
  if (version === SCHEMA_VERSION) {
    const sanitized = { ...saved };
    if (sanitized.store && sanitized.store.match && Array.isArray(sanitized.store.match.sets)) {
      sanitized.store.match.sets = sanitized.store.match.sets.map(set => {
        if (!set || !Array.isArray(set.points)) return set;
        return {
          ...set,
          points: set.points.map(sanitizePoint)
        };
      });
    }
    if (sanitized.app && sanitized.app.currentPoint) {
      sanitized.app.currentPoint = sanitizePoint(sanitized.app.currentPoint);
    }
    return sanitized;
  }
  if (version === 9) {
    return { ...saved, schemaVersion: SCHEMA_VERSION };
  }
  if (version === 8) {
    const next = { ...saved, schemaVersion: SCHEMA_VERSION };
    if (next.store && next.store.match && next.store.match.metadata) {
      next.store.match.metadata = {
        teamName: 'Moss Side Jaguars',
        ...next.store.match.metadata
      };
    }
    return next;
  }
  if (version <= 7) {
    const next = { ...saved, schemaVersion: SCHEMA_VERSION };
    if (next.store) {
      next.store = { ...next.store };
      if (Array.isArray(next.store.startingPlayers)) {
        next.store.currentLineup = migrateCurrentLineup(next.store.startingPlayers);
        delete next.store.startingPlayers;
      } else if (Array.isArray(next.store.rotationLineup)) {
        next.store.currentLineup = migrateCurrentLineup(next.store.rotationLineup);
        delete next.store.rotationLineup;
      } else if (Array.isArray(next.store.currentLineup)) {
        next.store.currentLineup = migrateCurrentLineup(next.store.currentLineup);
      }
      if (next.store.libero && !next.store.currentLibero) {
        next.store.currentLibero = next.store.libero;
      }
      if ('libero' in next.store) {
        delete next.store.libero;
      }
      if (next.store.match) {
        next.store.match = normalizeRoster(next.store.match);
      }
      if (next.store.match && Array.isArray(next.store.match.sets)) {
        next.store.match.sets = next.store.match.sets.map((set) => {
          if (!set || typeof set !== 'object') {
            return set;
          }
          if (!Array.isArray(set.startingLineup)) {
            return set;
          }
          const nextSet = {
            ...set,
            startingLineup: migrateCurrentLineup(set.startingLineup)
          };
          if (!nextSet.startingLibero && nextSet.libero) {
            nextSet.startingLibero = nextSet.libero;
          }
          if ('libero' in nextSet) {
            delete nextSet.libero;
          }
          if (Array.isArray(nextSet.points)) {
            nextSet.points = nextSet.points.map(normalizePointLiberoNumber).map(sanitizePoint);
          }
          return nextSet;
        });
      }
    }
    if (next.app) {
      const nextApp = { ...next.app };
      if (!nextApp.matchStartServeReceive && nextApp.selectedOption) {
        nextApp.matchStartServeReceive = nextApp.selectedOption;
      }
      if (!nextApp.matchTiebreakServeReceive && nextApp.currentPoint && nextApp.currentPoint.startPoint) {
        const isTieBreak = store.match.setsWon === 2 && store.match.setsLost === 2;
        if (isTieBreak) {
          nextApp.matchTiebreakServeReceive = nextApp.currentPoint.startPoint;
        }
      }
      if ('selectedOption' in nextApp) {
        delete nextApp.selectedOption;
      }
      if (nextApp.currentPoint) {
        nextApp.currentPoint = sanitizePoint(normalizePointLiberoNumber(nextApp.currentPoint));
      }
      next.app = nextApp;
    }
    return next;
  }
  return null;
};

export const createEmptyDuringRally = () => ({
  attacks: {},
  blocksSolo: {},
  blocksJoint: {},
  digs: {}
});

export const createEmptyDuringRallyEvents = () => [];

export const stripPlayerRef = (player) => {
  if (!player) {
    return player;
  }
  if (typeof player === 'string') {
    return player;
  }
  if (player.id !== undefined && player.id !== null) {
    return { id: player.id };
  }
  if (player.name) {
    return { name: player.name };
  }
  return {};
};

export const stripLineup = (lineup = []) => (
  lineup.map(item => ({
    ...item,
    player: stripPlayerRef(item.player)
  }))
);

export const stripPoint = (point) => {
  if (!point) {
    return point;
  }
  const next = { ...point };
  if (next.serveAction) {
    next.serveAction = { ...next.serveAction, player: stripPlayerRef(next.serveAction.player) };
  }
  if (next.receiveAction) {
    next.receiveAction = { ...next.receiveAction, player: stripPlayerRef(next.receiveAction.player) };
  }
  if (next.scoreAction) {
    next.scoreAction = {
      ...next.scoreAction,
      player: stripPlayerRef(next.scoreAction.player),
      secondPlayer: stripPlayerRef(next.scoreAction.secondPlayer)
    };
  }
  return next;
};

export const stripSet = (set) => {
  const next = { ...set };
  if (Array.isArray(set.startingLineup)) {
    next.startingLineup = stripLineup(set.startingLineup);
  }
  if (Array.isArray(set.points)) {
    next.points = set.points.map(stripPoint);
  }
  if (set.startingLibero) {
    next.startingLibero = stripPlayerRef(set.startingLibero);
  } else if (set.libero) {
    next.libero = stripPlayerRef(set.libero);
  }
  return next;
};

export const compactArchivedState = (state) => {
  if (!state) {
    return state;
  }
  const next = { ...state };
  if (next.store) {
    const match = next.store.match || {};
    next.store = {
      ...next.store,
      match: {
        ...match,
        sets: Array.isArray(match.sets) ? match.sets.map(stripSet) : []
      },
      currentLineup: stripLineup(next.store.currentLineup || []),
      currentLibero: stripPlayerRef(next.store.currentLibero)
    };
  }
  if (next.app) {
    next.app = {
      ...next.app,
      currentPoint: stripPoint(next.app.currentPoint || null)
    };
  }
  return next;
};

export const getDefaultCurrentPoint = () => ({
  id: 1,
  startPoint: '',
  serveAction: { player: {}, rating: null, call: null },
  receiveAction: { player: {}, rating: null, call: null },
  scoreAction: { outcome: null, pointDetail: '', player: {}, secondPlayer: null, attackSubtype: null, attackErrorSubtype: null },
  currentScore: { won: 0, lost: 0 },
  currentRotationCode: '',
  currentLineupNumbers: [],
  currentOnCourtNumbers: [],
  currentLiberoNumber: null,
  rallyActive: true,
  duringRally: createEmptyDuringRally(),
  duringRallyEvents: createEmptyDuringRallyEvents(),
  duringRallyRoster: {}
});
